import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { Exclude, Expose } from "class-transformer";
import { IsEnum, IsNumber, IsOptional, IsPositive } from "class-validator";

import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { ReferenceMeasureLength } from "@domain/project/floorplan/reference-measure-length";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export enum AirPathDirection {
  SUPPLY_AIR = "SUPPLY_AIR",
  EXHAUST_AIR = "EXHAUST_AIR",
}

export class AirPath extends FloorplanItem implements ReferenceMeasureLength {
  @IsNumber()
  @IsPositive({ message: "The value must be higher than 0" })
  @Expose({ name: "length" })
  private _length: number;

  @IsNumber()
  @Expose({ name: "strokeWidth" })
  private _strokeWidth: number;

  @IsNumber()
  @Expose({ name: "rotation" })
  private _rotation: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "referenceLength" })
  public _referenceLength?: number;

  @IsEnum(AirPathDirection)
  public direction: AirPathDirection;

  @Exclude()
  public lengthChanged: Subject<{ length: number }>;

  @Exclude()
  referenceLengthChanged: Subject<{ referenceLength: number | undefined }>;

  constructor(
    floorplan: Floorplan,
    id: string,
    direction: AirPathDirection,
    x: number,
    y: number,
    length: number = 150,
    strokeWidth: number = 20,
    rotation: number = 0,
  ) {
    super(floorplan, id, x, y);
    this._length = length;
    this._strokeWidth = strokeWidth;
    this._rotation = rotation;
    this.direction = direction;
    this.lengthChanged = new Subject();
    this.referenceLengthChanged = new Subject();
    this.updateReferenceLength(this._length);
  }

  get length(): number {
    return this._length;
  }

  get strokeWidth(): number {
    return this._strokeWidth;
  }

  get rotation(): number {
    return this._rotation;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const direction = this.direction;
    const length = this.length;
    const rotation = this.rotation;
    const strokeWidth = this.strokeWidth;

    return (x: number, y: number) => AirPath.create(this._floorplan, direction, x, y, length, strokeWidth, rotation);
  }

  override delete() {
    this.lengthChanged.unsubscribe();
    this._floorplan.deleteAirPath(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.airPaths;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as AirPath,
        this._floorplan.airPaths,
        FloorplanEventType.AIR_PATH_ADDED,
        () => this._floorplan.project.setAirPathPositionIds(),
        afterDuplicateAdded as ((duplicate: AirPath) => void) | undefined,
      );
  }

  get referenceLength(): number | undefined {
    return this._referenceLength;
  }

  updateLength(referenceLength: number) {
    this._length = referenceLength * this._floorplan.getReferenceScale();
    this.updateForm(this._length, this.strokeWidth, this._rotation, this.x, this.y);
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED_BY_INPUT, this);
  }

  updateReferenceLength(length: number) {
    if (this.isReferenceMeasureSet()) {
      this._referenceLength = Math.round((length / this._floorplan.getReferenceScale()) * 100) / 100;
    } else {
      this._referenceLength = undefined;
    }
  }

  updateForm(length: number, strokeWidth: number, rotation: number, x: number, y: number) {
    this.lengthChanged.next({ length: length });
    this.updateReferenceLength(length);
    this._length = length;
    this._strokeWidth = strokeWidth;
    this._rotation = rotation;
    this.updatePosition(x, y);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.airPathsItemTextVisible;
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshAirPathsLock();
  }

  static create(
    floorplan: Floorplan,
    direction: AirPathDirection,
    x: number,
    y: number,
    length: number,
    strokeWidth: number,
    rotation: number,
  ) {
    return new AirPath(floorplan, uuidv4(), direction, x, y, length, strokeWidth, rotation);
  }
}
